<script setup>
/**
 * One source, and what the assistant actually learned from it. THE PLAN, §4 P5.
 *
 * THIS PAGE IS A DIAGNOSIS, not a viewer. The retriever searches the generated
 * question/answer pairs and never the original text, so "it told a customer the wrong
 * delivery price" has exactly one place it can be explained — and until this screen
 * existed the only remedy was to delete the whole source and add it again and hope.
 *
 * Which is why the order is what it is: what it LEARNED first, at full size, and the
 * original text second, folded away. The document is the input; the pairs are what the
 * product actually runs on. A viewer would have put them the other way round.
 *
 * TWO READS, ONE OF WHICH CAN FAIL ALONE. Without the source there is no page, so its
 * failure is the page's failure — including the "not here any more" case, which is a
 * different sentence from "we could not reach the server" and gets one. The answers read
 * degrades in place: an owner looking at a source whose pairs could not be fetched is told
 * that, rather than shown an empty list that reads as "this taught it nothing" — which is
 * a real state, with a real amber warning, and must not be counterfeited by a timeout.
 *
 * THE TITLE IS NOT IN THE HEADER, deliberately. It is the owner's own text and it can be
 * Arabic in an English dashboard; the frame's header renders a plain string with no
 * isolate, so a name put there would exchange punctuation with the chrome around it. It
 * sits in the body instead, inside a `<bdi>`, where it can be as long and as bidirectional
 * as it likes.
 */
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import AnswerList from '../components/AnswerList.vue';
import WasloExplainer from '../components/WasloExplainer.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { answerCountLabel } from '../helpers/answer-count.js';
import { toIntlLocale } from '../helpers/intl.js';
import { wasloApi } from '../api/client.js';

const { t, locale } = useWasloI18n();
const accountId = useWasloAccountId();
const route = useRoute();
const router = useRouter();

/** The route is the only authority here, exactly as the account id is. */
const sourceId = computed(() => {
  const raw = route?.params?.sourceId;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
});

const source = useWasloApi(signal =>
  wasloApi.getDocument(accountId.value, sourceId.value, signal)
);
/**
 * Ease C3 (P-6.1): the list reads its own pages (`AnswerList` with this `documentId`), with
 * search and the controls to fix one. It hands each page up, and the count line reads it.
 */
const answersData = ref(null);
const listKey = ref(0);
const onAnswers = page => {
  answersData.value = page;
};

const errorMessage = computed(() => {
  if (sourceId.value === null) return t('WASLO.ANSWERS.SOURCE.NOT_FOUND');
  if (!source.error.value) return '';
  // "Gone" and "we could not reach it" are different facts and the owner acts on them
  // differently — one is a wrong link, the other is worth trying again.
  return source.error.value.code === 'not_found'
    ? t('WASLO.ANSWERS.SOURCE.NOT_FOUND')
    : t('WASLO.COMMON.ERROR_BODY');
});

const isFetching = computed(
  () => !errorMessage.value && source.isFetching.value
);

const backUrl = computed(() =>
  accountId.value === null
    ? ''
    : frontendURL(`accounts/${accountId.value}/waslo/answers`)
);

const title = computed(() => source.data.value?.title ?? '');
const content = computed(() => source.data.value?.content ?? '');
/** P-6.2: the answers saved one by one are never removed all together. */
const isReserved = computed(() => source.data.value?.reserved === true);

const formatCount = value =>
  new Intl.NumberFormat(toIntlLocale(locale.value)).format(value);

/** From the ANSWERS read, not the library row: this page is about what it taught. */
const answerCount = computed(() =>
  answersData.value
    ? answerCountLabel(t, answersData.value.total, formatCount)
    : ''
);

const reload = () => {
  source.reload();
  listKey.value += 1;
};

// ── removing it ────────────────────────────────────────────────────────────────────
// Behind a confirmation, because it takes everything the assistant learned with it until
// it is put back from Recently removed (P-6.11). The dialog says so in the sentence.
const removeRef = ref(null);
const isRemoving = ref(false);
const removeError = ref('');

const askRemove = () => {
  removeError.value = '';
  removeRef.value?.open();
};

const remove = async () => {
  if (isRemoving.value || accountId.value === null || sourceId.value === null) {
    return;
  }
  isRemoving.value = true;
  removeError.value = '';
  try {
    await wasloApi.deleteDocument(accountId.value, sourceId.value);
    removeRef.value?.close();
    useAlert(t('WASLO.ANSWERS.SOURCE.REMOVED'));
    // Back to the library, which refetches on mount — so the row is gone because the
    // server says it is gone, not because this page crossed it out optimistically.
    if (backUrl.value) router.push(backUrl.value);
  } catch {
    removeError.value = t('WASLO.ANSWERS.SOURCE.REMOVE_FAILED');
  } finally {
    isRemoving.value = false;
  }
};
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.ANSWERS.SOURCE.TITLE')"
    :back-url="backUrl"
    :back-label="t('WASLO.ANSWERS.TITLE')"
    :is-fetching="isFetching"
    :error-message="errorMessage"
    @retry="reload"
  >
    <template #body>
      <div class="flex flex-col gap-8 pb-10">
        <!-- The owner's own name for it, at full size, in its own direction. -->
        <header class="flex flex-col gap-2" data-waslo-source-header>
          <h2
            class="mb-0 text-2xl font-medium leading-8 break-words text-n-slate-12"
            data-waslo-source-name
          >
            <bdi>{{ title }}</bdi>
          </h2>
          <p v-if="answerCount" class="mb-0 text-sm text-n-slate-11">
            {{ answerCount }}
          </p>
        </header>

        <WasloExplainer :note="t('WASLO.ANSWERS.SOURCE.SPOTLIGHT')" />

        <!-- 1 · WHAT IT LEARNED. First, and at full size: this is what the assistant
             actually searches. -->
        <section class="flex flex-col gap-4" data-waslo-source-answers>
          <SettingsHeader
            :heading="t('WASLO.ANSWERS.SOURCE.ANSWERS_HEADING')"
            :description="t('WASLO.ANSWERS.SOURCE.ANSWERS_DESCRIPTION')"
          />
          <AnswerList
            :key="listKey"
            :account-id="accountId"
            :document-id="sourceId"
            @loaded="onAnswers"
          />
        </section>

        <span class="w-full h-px bg-n-weak" />

        <!-- 2 · WHAT WE READ. Folded away: it is the input, not the product. -->
        <section class="flex flex-col gap-4" data-waslo-source-original>
          <Accordion
            :title="t('WASLO.ANSWERS.SOURCE.ORIGINAL')"
            :is-open="false"
          >
            <div class="flex flex-col gap-3 pt-2">
              <p class="mb-0 text-sm text-n-slate-11">
                {{ t('WASLO.ANSWERS.SOURCE.ORIGINAL_HINT') }}
              </p>
              <!--
                `whitespace-pre-wrap` + `break-words`, and never a `<pre>`: an uploaded
                catalogue routinely contains one unbroken 400-character token, and a
                pre-formatted block blows the page out sideways on a phone rather than
                scrolling inside itself. The same incident is written up in the styles
                sheet this section replaces.
              -->
              <p
                class="mb-0 overflow-x-auto text-sm leading-6 break-words whitespace-pre-wrap max-h-96 text-n-slate-11"
                dir="auto"
                data-waslo-source-text
              >
                {{ content }}
              </p>
            </div>
          </Accordion>
        </section>

        <span class="w-full h-px bg-n-weak" />

        <!-- 3 · REMOVING IT. Last, quiet, and behind a question. The answers saved one by one
             are never removed all together (P-6.2), so that one source says so instead. -->
        <section
          class="flex flex-col items-start gap-3"
          data-waslo-source-remove
        >
          <p
            v-if="isReserved"
            class="mb-0 text-sm text-n-slate-11"
            data-waslo-source-reserved
          >
            {{ t('WASLO.ANSWERS.SOURCE.RESERVED_NOTE') }}
          </p>
          <Button
            v-else
            :label="t('WASLO.ANSWERS.SOURCE.REMOVE')"
            variant="faded"
            color="ruby"
            size="sm"
            icon="i-lucide-trash-2"
            data-waslo-source-remove-open
            @click="askRemove"
          />
        </section>
      </div>
    </template>
  </WasloPageLayout>

  <Dialog
    ref="removeRef"
    type="alert"
    :title="t('WASLO.ANSWERS.SOURCE.REMOVE_TITLE')"
    :description="t('WASLO.ANSWERS.SOURCE.REMOVE_BODY')"
    :confirm-button-label="t('WASLO.ANSWERS.SOURCE.REMOVE_CONFIRM')"
    :cancel-button-label="t('WASLO.ANSWERS.DIALOG.CANCEL')"
    :is-loading="isRemoving"
    :disable-confirm-button="isRemoving"
    data-waslo-source-remove-dialog
    @confirm="remove"
  >
    <Banner v-if="removeError" color="ruby" data-waslo-source-remove-error>
      {{ removeError }}
    </Banner>
  </Dialog>
</template>
